// frontend/src/utils/sentry.js
//
// Wrapper Sentry pour le frontend React. Pattern aligné avec le backend
// (utils/sentry.js + utils/monitoring.js) :
//   - require statique de @sentry/react (deps obligatoire à l'install)
//   - init no-op si VITE_SENTRY_DSN absent OU mode dev (pas de bruit local)
//   - jamais throw, fonctions exposées sont des no-ops si non initialisé
//
// Activation :
//   1. npm install @sentry/react
//   2. VITE_SENTRY_DSN=https://xxx@sentry.io/yyy dans frontend/.env
//   3. npm run build
//
// Test bout-en-bout : `Sentry.ErrorBoundary` capture les crashes React.
// Pour tester manuellement : ajouter `throw new Error('sentry test')`
// dans un composant et vérifier la remontée dans le dashboard.

import * as Sentry from '@sentry/react'

let initialized = false

export function initSentry() {
  if (initialized) return
  // Pas de capture en dev — bruit inutile + DSN gratuit limité à 5k events/mois.
  if (!import.meta.env.PROD) return
  const dsn = import.meta.env.VITE_SENTRY_DSN
  if (!dsn) return

  try {
    Sentry.init({
      dsn,
      environment: import.meta.env.MODE,
      release: import.meta.env.VITE_APP_VERSION || 'terrilink-frontend@unknown',
      // Sample rates conservateurs pour ne pas saturer le quota gratuit.
      tracesSampleRate: 0.1,
      replaysSessionSampleRate: 0.0,
      replaysOnErrorSampleRate: 1.0,
      // Tag par défaut pour distinguer front vs back dans Sentry.
      initialScope: {
        tags: { app: 'terrilink-frontend', platform: 'frontend' },
      },
      // Filtrer le bruit : erreurs de réseau bénignes côté browser.
      // Patterns volontairement étroits — toute erreur réseau générique
      // (Network Error, Load failed, NetworkError) reste capturée, sinon
      // un vrai outage /api/auth/me ou /socket.io passerait inaperçu.
      ignoreErrors: [
        'ResizeObserver loop limit exceeded',
        'ResizeObserver loop completed with undelivered notifications',
        'Non-Error promise rejection captured',
        // Service Worker / PWA : le browser tente de mettre à jour le SW en
        // arrière-plan ; un fetch transitoire qui échoue (4G→5G, hibernation,
        // 502/504 Nginx ponctuel) déclenche une unhandledrejection. Le browser
        // retry tout seul, l'utilisateur ne voit rien. Pas actionnable.
        'Failed to update a ServiceWorker',
        'Failed to register a ServiceWorker',
        'ServiceWorker script evaluation failed',
        // Navigation pendant un fetch en cours (AbortController) — comportement
        // normal quand l'utilisateur change de page avant la fin d'une requête.
        'The operation was aborted',
        // Browser plugins / extensions qui injectent du JS et plantent.
        'extension://',
        'chrome-extension://',
      ],
      beforeSend(event, hint) {
        // Filtre les erreurs avec status 4xx (auth expirée, validation côté
        // serveur). Ces erreurs sont attendues et gérées par le métier.
        const status = hint?.originalException?.response?.status
        if (status && status >= 400 && status < 500) return null
        return event
      },
    })
    initialized = true
    // eslint-disable-next-line no-console
    console.log('✅ Sentry frontend initialisé')
  } catch (err) {
    // Init Sentry ne doit jamais empêcher le boot du SPA.
    // eslint-disable-next-line no-console
    console.warn('⚠️  Sentry frontend init failed (non bloquant):', err.message)
  }
}

// setUser : appelé après login (depuis userActions.fetchUser).
// Le user reste attaché à la scope globale jusqu'au prochain setUser ou clearUser.
export function setUser(user) {
  if (!initialized || !user) return
  try {
    Sentry.setUser({
      id: user.id,
      email: user.email,
      organizationSlug: user.organizationSlug,
      role: user.role,
    })
    // Tag tenant_slug aussi côté front pour parité avec backend.
    if (user.organizationSlug) {
      Sentry.getCurrentScope?.()?.setTag?.('tenant_slug', user.organizationSlug)
    }
  } catch {
    // Defense in depth — observabilité ne doit jamais casser l'app
  }
}

export function clearUser() {
  if (!initialized) return
  try {
    Sentry.setUser(null)
  } catch {
    // no-op
  }
}

// Accepte soit un objet flat (legacy : on l'enveloppe dans `extra`), soit
// un hint Sentry structuré ({ contexts, tags, extra, level, ... }) qu'on
// passe tel quel. Permet aux appelants comme ErrorBoundary de remonter
// `contexts.react.componentStack` au bon format (groupage Sentry React-aware)
// sans casser les anciens appels du type `captureException(err, { reason })`.
export function captureException(error, options = {}) {
  if (!initialized) return
  try {
    const isStructuredHint =
      options &&
      typeof options === 'object' &&
      !Array.isArray(options) &&
      ('contexts' in options ||
        'tags' in options ||
        'extra' in options ||
        'level' in options ||
        'fingerprint' in options ||
        'user' in options)
    const hint = isStructuredHint ? options : { extra: options }
    Sentry.captureException(error, hint)
  } catch {
    // no-op
  }
}

// ErrorBoundary réexporté tel quel — wrapper autour de <App /> dans index.js.
// Si Sentry n'est pas init, le boundary fonctionne quand même (capture locale)
// mais ne remonte rien.
export const ErrorBoundary = Sentry.ErrorBoundary
